import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ScrollLines, Reveal, Eyebrow } from '@/components/Reveal';
import CtaBand from '@/components/CtaBand';
import GradeGuide from '@/components/GradeGuide';
import { SPICES } from '@/data/spices';

export default function Products() {
  return (
    <div className="bg-night text-bone">
      <header className="mx-auto max-w-7xl px-6 pb-16 pt-40 md:px-12 md:pb-20 md:pt-48">
        <Reveal>
          <Eyebrow>The catalogue</Eyebrow>
        </Reveal>
        <ScrollLines
          className="mt-6 block"
          lineClassName="font-serif text-5xl font-medium leading-[1.0] tracking-tight text-bone sm:text-6xl lg:text-7xl"
          lines={['Two spices.', 'Every grade.']}
        />
        <Reveal delay={0.3} className="mt-8 max-w-2xl">
          <p className="text-base leading-relaxed text-dune md:text-lg">
            Turmeric and cardamom, farm gate to vessel. Indicative grades and specs below — every
            lot is quoted against your market, your season and your shipment window. No public
            prices; the number comes with the quote.
          </p>
        </Reveal>
      </header>

      <div className="mx-auto max-w-7xl space-y-24 px-6 pb-24 md:space-y-32 md:px-12 md:pb-32">
        {SPICES.map((s, i) => (
          <section key={s.slug} data-testid={`product-section-${s.slug}`}>
            <div className="grid items-center gap-10 md:grid-cols-12 md:gap-14">
              <Reveal className={`md:col-span-5 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                <div className="group relative overflow-hidden rounded-t-[12rem] rounded-b-3xl border border-white/10">
                  <img
                    src={s.image}
                    alt={`${s.name} — ${s.latin}`}
                    loading="lazy"
                    className="h-[420px] w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105 md:h-[520px]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-night/60 via-transparent to-transparent" />
                  <span className="absolute bottom-6 right-6 rounded-full border border-bone/25 bg-night/50 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-bone backdrop-blur-sm">
                    {s.chip}
                  </span>
                </div>
              </Reveal>

              <div className={`md:col-span-7 ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <Reveal delay={0.1}>
                  <span className="font-serif text-lg italic text-turmeric">0{i + 1}</span>
                  <h2 className="mt-2 font-serif text-4xl font-medium text-bone md:text-5xl lg:text-6xl">
                    {s.name}
                  </h2>
                  <p className="mt-2 font-serif text-xl italic text-dune">{s.latin}</p>
                  <p className="mt-5 max-w-xl text-sm leading-relaxed text-bone/80 md:text-base">
                    {s.tagline}
                  </p>
                  <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.26em] text-turmeric">
                    Origin — {s.origin}
                  </p>
                </Reveal>

                <Reveal delay={0.2} className="mt-8">
                  <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2">
                    {s.specs.map(([k, v]) => (
                      <div key={k} className="bg-coal/80 p-5">
                        <dt className="text-[10px] font-medium uppercase tracking-[0.22em] text-dune">{k}</dt>
                        <dd className="mt-2 text-sm text-bone md:text-base">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </Reveal>

                <Reveal delay={0.25} className="mt-6">
                  <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-dune">Varieties &amp; grades</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`varieties-${s.slug}`}>
                    {s.varieties.map((v) => (
                      <span key={v} className="rounded-full border border-white/15 bg-coal/60 px-4 py-1.5 text-[12px] text-bone/85">
                        {v}
                      </span>
                    ))}
                  </div>
                </Reveal>

                <Reveal delay={0.3} className="mt-8">
                  <Link
                    to={`/contact?spice=${s.name}`}
                    data-testid={`product-quote-cta-${s.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-turmeric/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-turmeric transition-all duration-300 hover:bg-turmeric hover:text-night"
                  >
                    Request a quote for {s.name}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </section>
        ))}

        <Reveal>
          <p className="mx-auto max-w-2xl text-center text-sm leading-relaxed text-dune">
            Seasonal market sheets, crop outlooks and price trend notes for each origin are shared
            with active buyers — ask for them with your enquiry.
          </p>
        </Reveal>
      </div>

      <GradeGuide />

      <CtaBand />
    </div>
  );
}
